/**
 * REGIME POR NÍVEL (pedido 08/09): regime tributário definível em bot, grupo e
 * departamento na aba Dependências, com HERANÇA departamento → grupo → bot — o
 * mais específico vence. O regime efetivo age como FILTRO das empresas exibidas
 * nas visões (nunca muda dado, só recorta exibição).
 *
 * A herança é CONTEXTUAL POR CADEIA: com multi-grupo, o mesmo bot pode herdar
 * regimes diferentes quando aparece dentro de grupos diferentes (macro/board) —
 * intencional. Fora de qualquer grupo (trilhos do Resumo, "Bots sem grupo"),
 * vale só o regime PRÓPRIO do bot, sem herança.
 *
 * Comparação sempre pelos LABELS normalizados que o /resumo já entrega (ex.
 * "Simples Nacional", "Lucro Presumido", "Outros") — nunca por código.
 */

import type { IEmpresaInfo } from '../api';

/** Qualquer nível configurável (bot/grupo/departamento) — só o campo importa. */
export interface INivelComRegime {
  regime?: string | null;
}

/**
 * Um nível só "declara" regime quando o valor tem conteúdo: null, undefined,
 * string vazia e espaços são AUSÊNCIA (o nível herda), e o label vem trimado.
 *
 * Por que não basta `??`: `??` só pula null/undefined, então `bot.regime = ''`
 * VENCERIA o regime do grupo e viraria um filtro que nenhuma empresa casa
 * (`empresaBateRegime` é fail-closed) — a lista esvazia enquanto a bolha de
 * porcentagem, calculada no backend, segue mostrando o número do regime do grupo.
 * Bolha discordando da lista é exatamente o bug que esta tela não pode repetir.
 *
 * `organizacao_service._normalizar_regime` limpa na ESCRITA, mas só no caminho do
 * CRUD: documento antigo e `aplicar_snapshot` (que grava `regime` sem normalizar)
 * podem ter `''` no banco. A leitura precisa ser tolerante dos dois lados.
 *
 * Espelho EXATO de `cobertura_service.regime_efetivo_na_cadeia` no backend — as
 * duas resolvem a mesma herança e precisam concordar valor a valor, inclusive em
 * `''` e `'   '`.
 */
const declarado = (nivel: INivelComRegime | null | undefined): string | null => {
  const valor = nivel?.regime;
  if (valor === null || valor === undefined) return null;
  const limpo = String(valor).trim();
  return limpo || null;
};

/**
 * Regime efetivo do bot DENTRO de uma cadeia grupo/departamento (macro/board):
 * bot.regime → grupo.regime → departamento.regime, o mais específico que DECLARA
 * (ver `declarado`). Campo ausente (BFF antigo) e nível ausente contam como null
 * (herda / sem restrição).
 */
export const regimeEfetivoNaCadeia = (
  bot: INivelComRegime | null | undefined,
  grupo: INivelComRegime | null | undefined,
  departamento: INivelComRegime | null | undefined,
): string | null => declarado(bot) ?? declarado(grupo) ?? declarado(departamento);

/**
 * Regime do bot em contexto SEM grupo (trilhos do Resumo, "Bots sem grupo"):
 * só o regime próprio — nunca herda, não há cadeia.
 */
export const regimeProprio = (bot: INivelComRegime | null | undefined): string | null =>
  declarado(bot);

/**
 * A empresa passa pelo filtro do regime efetivo? Sem regime efetivo (null/
 * undefined) não há filtro — passa tudo. Com regime definido, exige match exato
 * do label; empresa sem cadastro/sem regime NÃO passa (fail-closed, mesma regra
 * do filtro global de regime).
 */
export const empresaBateRegime = (
  empresa: Pick<IEmpresaInfo, 'regime'> | null | undefined,
  regimeEfetivo: string | null | undefined,
): boolean => {
  if (regimeEfetivo === null || regimeEfetivo === undefined) return true;
  return empresa?.regime === regimeEfetivo;
};

/** O que o bot herdaria (regime próprio null) considerando TODOS os seus grupos. */
export type HerancaRegimeBot =
  | { tipo: 'nenhuma' }
  | { tipo: 'unica'; regime: string }
  | { tipo: 'varia' };

/**
 * Herança exibida no card de bots da aba Dependências: para cada grupo do bot,
 * o herdado é grupo.regime ?? departamento.regime. Sem grupo (ou tudo null) →
 * 'nenhuma'; um único valor → 'unica'; heranças divergentes entre grupos →
 * 'varia' ("herda: varia por grupo" — a herança real é contextual por cadeia).
 */
export const herancaRegimeDoBot = (
  grupoIds: string[],
  grupoPorId: ReadonlyMap<string, INivelComRegime & { departamento_id: string | null }>,
  departamentoPorId: ReadonlyMap<string, INivelComRegime>,
): HerancaRegimeBot => {
  const herdados = new Set<string | null>(
    grupoIds.map(id => {
      const grupo = grupoPorId.get(id);
      const departamento = grupo?.departamento_id
        ? departamentoPorId.get(grupo.departamento_id)
        : undefined;
      return regimeEfetivoNaCadeia(null, grupo, departamento);
    }),
  );
  if (herdados.size > 1) return { tipo: 'varia' };
  const unico = herdados.size === 1 ? (herdados.values().next().value ?? null) : null;
  return unico === null ? { tipo: 'nenhuma' } : { tipo: 'unica', regime: unico };
};
